import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import { HighlightLayer } from "@babylonjs/core/Layers/highlightLayer";
import "@babylonjs/core/Layers/effectLayerSceneComponent"; // side-effect required by HighlightLayer
import { Observer } from "@babylonjs/core/Misc/observable";
import type { Scene } from "@babylonjs/core/scene";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { createShapeMesh, mat } from "./meshes";
import { InteractionSystem, type InteractionEvent } from "./InteractionSystem";
import { openQuiz } from "./PanneauBiblique";
import type { GenesisEngine } from "./GenesisEngine";
import type { AudioSystem } from "./AudioSystem";
import type { Hud, Difficulty } from "./Hud";
import type { InteractableDef, PanelDef, QuizDef, SceneDef } from "./schema";
import { MultiplayerSystem, peerColor, type PeerInfo } from "./MultiplayerSystem";
import { t } from "./i18n";

const ADVANCING = new Set(["drag_drop", "guide_target", "pickup", "lever_action"]);

/** WorldManager — renders a SceneDef and runs its objective loop. */
export class WorldManager {
  private built: TransformNode[] = [];
  private highlight: HighlightLayer | null = null;
  private renderObs: Observer<Scene> | null = null;
  private keys = new Set<string>();
  private player: Mesh | null = null;
  private progress = 0;
  private goal = 1;
  private def: SceneDef | null = null;
  private difficulty: Difficulty = "D";
  private onExit: () => void = () => {};
  private completed = false;
  /** id → mesh for every interactable (world-event targets). */
  private targets = new Map<string, Mesh>();
  /** id → def for every interactable. */
  private idefs = new Map<string, InteractableDef>();
  /** advancing interactables already counted (no double-count on re-click). */
  private consumed = new Set<string>();
  /** v7 — per-session randomness: every playthrough differs (seeded PRNG). */
  private rand: () => number = Math.random;
  private nextAmbientAt = 45;
  /** touch/mobile: tap-to-move destination (tap the ground → walk there). */
  private moveTarget: Vector3 | null = null;
  private groundMesh: Mesh | null = null;
  /** v8 — physical obstacles: solid props/anchors the player cannot cross. */
  private solids: Array<{ x: number; z: number; r: number }> = [];
  private pointerObs: Observer<import("@babylonjs/core/Events/pointerEvents").PointerInfo> | null = null;
  /** v9 multiplayer — remote pilgrim avatars (peerId → mesh + label + lerp state). */
  private peerAvatars = new Map<
    string,
    { root: Mesh; label: TextBlock; cur: { x: number; z: number; ry: number }; target: { x: number; z: number; ry: number } }
  >();
  /** v9 — gamepad detection is logged once per WorldManager lifetime. */
  private gamepadLogged = false;

  constructor(
    private readonly gen: GenesisEngine,
    private readonly hud: Hud,
    private readonly audio: AudioSystem,
    /** v9 multiplayer — null in solo mode (no socket, zero overhead). */
    private readonly mp: MultiplayerSystem | null = null,
  ) {}

  run(def: SceneDef, difficulty: Difficulty, onExit: () => void): void {
    this.def = def;
    this.difficulty = difficulty;
    this.onExit = onExit;
    this.build();

    this.hud.onDifficulty = (d) => {
      this.difficulty = d;
      this.teardown();
      this.build();
    };
    this.hud.onFullscreen = () => this.gen.toggleFullscreen();
    this.hud.onMute = () => this.audio.toggleMute();
    this.hud.onExit = () => {
      this.teardown();
      this.onExit();
    };

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  }

  private build(): void {
    const scene = this.gen.scene;
    const def = this.def!;
    this.progress = 0;
    this.completed = false;

    // v7 — new session seed each build: layouts, goals, quizzes and ambient
    // events all differ between sessions (mulberry32 for in-session coherence).
    let seed = (Math.random() * 0xffffffff) >>> 0;
    this.rand = () => {
      seed = (seed + 0x6d2b79f5) >>> 0;
      let t = seed;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    this.nextAmbientAt = 40 + this.rand() * 35;

    this.gen.applyLighting(def.env.lighting, def.env.skyColor);
    this.audio.setAmbience(def.audio.ambience);
    this.hud.setTitle(t(def.title));
    this.highlight = new HighlightLayer("hl", scene);
    this.targets.clear();
    this.idefs.clear();
    this.consumed.clear();

    // Ground (receives the soft shadows; pickable for touch tap-to-move)
    const ground = createShapeMesh(scene, "box", def.env.groundColor, 1);
    ground.scaling = new Vector3(def.env.groundSize, 0.2, def.env.groundSize);
    ground.position.y = -0.1;
    ground.isPickable = true;
    ground.receiveShadows = true;
    this.built.push(ground);
    this.groundMesh = ground;
    const castShadow = (m: Mesh) => this.gen.shadows?.addShadowCaster(m, true);

    // Decorative props (with optional scatter). Solid shapes become physical
    // OBSTACLES (v8) the player must walk around — real terrain, not decor.
    const SOLID_R: Record<string, number> = { mountain: 2.6, tree: 0.55, stele: 0.7, ark: 3.4, gate: 0.9, box: 0.8, cylinder: 0.7 };
    this.solids = [];
    for (const p of def.env.props) {
      const copies = p.scatter ?? 1;
      const rad = p.scatterRadius ?? 4;
      for (let i = 0; i < copies; i++) {
        const m = createShapeMesh(scene, p.shape, p.color, p.size);
        const ang = (i / copies) * Math.PI * 2 + i * 2.399;
        const r = copies > 1 ? (0.35 + ((i * 37) % 100) / 150) * rad : 0;
        m.position = new Vector3(p.pos[0] + Math.cos(ang) * r, p.pos[1], p.pos[2] + Math.sin(ang) * r);
        m.isPickable = false;
        castShadow(m);
        this.built.push(m);
        const sr = SOLID_R[p.shape];
        if (sr) this.solids.push({ x: m.position.x, z: m.position.z, r: sr * p.size * 0.6 });
      }
    }

    // Targets + panels maps
    const targets = this.targets;
    const panels = new Map<string, PanelDef>();
    def.panels.forEach((p) => panels.set(p.id, p));

    // Build interactable meshes first (so drag targets exist)
    const interactMeshes: Array<{ mesh: Mesh; def: InteractableDef }> = [];
    const half = def.env.groundSize / 2 - 2;
    for (const idef of def.interactables) {
      const m = createShapeMesh(scene, idef.shape, idef.color, idef.size);
      // v7 — session layout variation: movable items (pickups, drag sources,
      // NPCs) jitter around their authored spot; anchors (build_slot targets,
      // panels, levers) stay put so mechanics remain readable.
      const movable = idef.type === "pickup" || idef.type === "drag_drop" || idef.type === "npc_dialog" || idef.type === "guide_target";
      const jx = movable ? (this.rand() - 0.5) * 5 : 0;
      const jz = movable ? (this.rand() - 0.5) * 5 : 0;
      m.position = new Vector3(clamp(idef.pos[0] + jx, -half, half), idef.pos[1], clamp(idef.pos[2] + jz, -half, half));
      targets.set(idef.id, m);
      this.idefs.set(idef.id, idef);
      castShadow(m);
      interactMeshes.push({ mesh: m, def: idef });
      this.built.push(m);
    }

    // SPIRITUAL KIT (v5): every scene gets prayer, verse-declaration, faith
    // and fruits-of-the-Spirit activities — engine-side, all 60 scenes at once.
    // Kit ids are "kit_"-prefixed and NEVER advance scene objectives.
    for (const kdef of this.spiritualKitDefs(def)) {
      const m = createShapeMesh(scene, kdef.shape, kdef.color, kdef.size);
      m.position = new Vector3(kdef.pos[0], kdef.pos[1], kdef.pos[2]);
      targets.set(kdef.id, m);
      this.idefs.set(kdef.id, kdef);
      castShadow(m);
      interactMeshes.push({ mesh: m, def: kdef });
      this.built.push(m);
    }
    for (const p of this.spiritualKitPanels(def)) panels.set(p.id, p);

    // Player avatar
    const player = createShapeMesh(scene, "person", "#3f6fb0", 1);
    player.position = new Vector3(def.spawn[0], def.spawn[1], def.spawn[2]);
    this.player = player;
    castShadow(player);
    this.built.push(player);
    this.gen.camera.lockedTarget = player;

    // v9 MULTIPLAYER — group mode: wire the relay callbacks for THIS scene and
    // spawn any pilgrims already known here (scene chain / late build). Solo
    // (mp === null) skips everything — zero overhead.
    if (this.mp) {
      const mp = this.mp;
      mp.setScene(def.id);
      mp.onPeerUpsert = (p) => this.upsertPeerAvatar(p);
      mp.onPeerLeft = (id) => this.removePeerAvatar(id);
      mp.onRemoteProgress = (iid, from, sceneId) => this.foldRemoteProgress(iid, from, sceneId);
      mp.onStatus = (connected, n) => this.hud.setPeers(n, mp.code, connected);
      this.hud.setPeers(mp.peerCount, mp.code, mp.connected);
      for (const p of mp.peersInScene(def.id)) this.upsertPeerAvatar(p);
    }

    // Wire interactivity
    const interaction = new InteractionSystem({
      scene,
      hud: this.hud,
      highlight: this.highlight,
      targets,
      panels,
      onEvent: (e) => this.handleEvent(e),
    });
    for (const { mesh, def: idef } of interactMeshes) interaction.attach(mesh, idef);

    // Objective goal from difficulty — v7: varies per session (±2) and ADAPTS
    // to the player's history (2+ completions of this game → higher bar).
    const advancing = def.interactables.filter((i) => ADVANCING.has(i.type)).length;
    let wanted = def.difficulty[this.difficulty]?.goalCount ?? advancing;
    wanted += Math.floor(this.rand() * 5) - 2; // session variation −2..+2
    const completions = this.perfCompletions(def.game);
    if (completions >= 2) wanted += 2; // adaptive: seasoned players get more
    this.goal = Math.max(1, Math.min(wanted, advancing || 1));
    this.updateObjective();

    // First tip — v7: drawn at random from the scene's tip pool; struggling
    // players (recent wrong quiz) get a second helping tip shortly after.
    const hints = def.difficulty[this.difficulty]?.hints ?? true;
    if (def.tips.length && hints) {
      const tip = def.tips[Math.floor(this.rand() * def.tips.length)];
      setTimeout(() => this.hud.showTip(tip), 900);
      if (this.perfLastQuizWrong() && def.tips.length > 1) {
        const tip2 = def.tips[Math.floor(this.rand() * def.tips.length)];
        setTimeout(() => this.hud.showTip(tip2), 8000);
      }
    }

    // LIVING WORLD (v4): idle animations for every mesh kind + wandering
    // agents — one engine pass animates all 60 scenes at once.
    const animated: Array<{ m: Mesh; kind: string; seed: number; home: Vector3 }> = [];
    for (const { mesh, def: idef } of interactMeshes) {
      if (idef.shape === "animal" || idef.shape === "person" || idef.shape === "flame" || idef.shape === "water") {
        animated.push({ m: mesh, kind: idef.shape, seed: Math.random() * 6.28, home: mesh.position.clone() });
      }
    }
    // props too (crowds breathe, trees sway via slight rotation, flames flicker)
    for (const n of this.built) {
      const nm = n.name || "";
      if (n instanceof Mesh && (nm.startsWith("abody") || nm.startsWith("body") || nm.startsWith("flame") || nm.startsWith("trunk") || nm.startsWith("water"))) {
        const kind = nm.startsWith("abody") ? "animal" : nm.startsWith("body") ? "person" : nm.startsWith("flame") ? "flame" : nm.startsWith("trunk") ? "tree" : "water";
        animated.push({ m: n, kind, seed: Math.random() * 6.28, home: n.position.clone() });
      }
    }
    let tAnim = 0;

    // Cinematic intro sweep: camera glides from high orbit down to the player.
    const cam = this.gen.camera;
    const introFrom = { alpha: cam.alpha + 1.4, beta: 0.9, radius: 54 };
    const introTo = { alpha: cam.alpha, beta: cam.beta, radius: cam.radius };
    let introK = 0;

    // TOUCH / MOBILE (v6): tap the ground → the player walks there (works
    // with mouse too). TAP only fires without drag, so camera orbit is safe.
    this.pointerObs = scene.onPointerObservable.add((pi) => {
      if (pi.type !== PointerEventTypes.POINTERTAP) return;
      const pick = pi.pickInfo;
      if (pick?.hit && pick.pickedMesh === this.groundMesh && pick.pickedPoint) {
        this.moveTarget = pick.pickedPoint.clone();
      }
    });

    // Player movement loop + living-world animation
    this.renderObs = scene.onBeforeRenderObservable.add(() => {
      const dt = this.gen.scene.getEngine().getDeltaTime() / 1000;
      tAnim += dt;
      if (introK < 1) {
        introK = Math.min(1, introK + dt / 2.6);
        const e = 1 - Math.pow(1 - introK, 3); // ease-out cubic
        cam.alpha = introFrom.alpha + (introTo.alpha - introFrom.alpha) * e;
        cam.beta = introFrom.beta + (introTo.beta - introFrom.beta) * e;
        cam.radius = introFrom.radius + (introTo.radius - introFrom.radius) * e;
      }
      // v7 — random ambient events: every 40-75s something LIVES (a golden
      // glow draws the eye to a random creature; sometimes a word of
      // encouragement) — no two sessions feel the same.
      if (tAnim > this.nextAmbientAt && animated.length) {
        this.nextAmbientAt = tAnim + 40 + this.rand() * 35;
        const pickA = animated[Math.floor(this.rand() * animated.length)];
        if (pickA?.m.isEnabled()) {
          this.highlight?.addMesh(pickA.m, Color3.FromHexString("#ffe08a"));
          setTimeout(() => this.highlight?.removeMesh(pickA.m), 2000);
        }
        if (this.rand() < 0.5) {
          const POOL = [
            { fr: "« Fortifie-toi et prends courage ! » (Josué 1:9)", en: "'Be strong and courageous!' (Joshua 1:9)" },
            { fr: "« L'Éternel est mon berger : je ne manquerai de rien. » (Ps 23:1)", en: "'Yahweh is my shepherd: I shall lack nothing.' (Ps 23:1)" },
            { fr: "« Je puis tout par celui qui me fortifie. » (Ph 4:13)", en: "'I can do all things through Christ who strengthens me.' (Php 4:13)" },
            { fr: "« Cherchez premièrement le royaume de Dieu. » (Mt 6:33)", en: "'Seek first God's Kingdom.' (Mt 6:33)" },
            { fr: "« La joie de l'Éternel sera votre force. » (Né 8:10)", en: "'The joy of Yahweh is your strength.' (Neh 8:10)" },
          ];
          this.hud.showTip(POOL[Math.floor(this.rand() * POOL.length)]);
        }
      }
      for (const a of animated) {
        if (!a.m.isEnabled()) continue;
        const t = tAnim + a.seed;
        switch (a.kind) {
          case "animal": // graze-bob + slow wander around home
            a.m.position.y = a.home.y + Math.abs(Math.sin(t * 2.2)) * 0.06;
            a.m.position.x = a.home.x + Math.sin(t * 0.25) * 0.8;
            a.m.position.z = a.home.z + Math.cos(t * 0.19) * 0.8;
            a.m.rotation.y = Math.sin(t * 0.25) * 0.6;
            break;
          case "person": // breathe + tiny sway
            a.m.scaling.y = 1 + Math.sin(t * 1.6) * 0.02;
            a.m.rotation.y = Math.sin(t * 0.4) * 0.15;
            break;
          case "flame": // flicker
            a.m.scaling.y = 1 + Math.sin(t * 9) * 0.12 + Math.sin(t * 23) * 0.05;
            a.m.scaling.x = 1 + Math.sin(t * 11 + 1) * 0.07;
            break;
          case "tree": // sway in the wind
            a.m.rotation.z = Math.sin(t * 0.8) * 0.03;
            break;
          case "water": // gentle swell
            a.m.position.y = a.home.y + Math.sin(t * 1.1) * 0.05;
            break;
        }
      }
      this.movePlayer();
      // v9 multiplayer — smooth remote pilgrims + broadcast our own position
      // (throttled to ~10 Hz inside MultiplayerSystem).
      this.updatePeerAvatars(dt);
      if (this.mp && this.player) {
        this.mp.sendPos(this.player.position.x, this.player.position.z, this.player.rotation.y);
      }
    });
  }

  /* ── v9 multiplayer — remote pilgrim avatars ─────────────────────────── */

  /** Spawn/refresh a peer's avatar (distinct color + floating name label). */
  private upsertPeerAvatar(p: PeerInfo): void {
    const def = this.def;
    if (!def) return;
    const inScene = p.hasPos && p.sceneId === def.id;
    const existing = this.peerAvatars.get(p.id);
    if (!inScene) {
      // Peer is elsewhere in the parcours (or has no position yet).
      if (existing) this.removePeerAvatar(p.id);
      return;
    }
    if (!existing) {
      const scene = this.gen.scene;
      const root = createShapeMesh(scene, "person", peerColor(p.id), 1);
      root.position = new Vector3(p.x, def.spawn[1], p.z);
      root.rotation.y = p.ry;
      root.isPickable = false;
      root.getChildMeshes(false).forEach((c) => (c.isPickable = false));
      this.gen.shadows?.addShadowCaster(root, true);
      const label = new TextBlock("peer_" + p.id, p.name || t({ fr: "Pèlerin", en: "Pilgrim" }));
      label.color = "#ffffff";
      label.fontSize = 14;
      label.outlineWidth = 4;
      label.outlineColor = "#0a1a26";
      this.hud.adt.addControl(label);
      label.linkWithMesh(root);
      label.linkOffsetY = -58;
      this.peerAvatars.set(p.id, {
        root,
        label,
        cur: { x: p.x, z: p.z, ry: p.ry },
        target: { x: p.x, z: p.z, ry: p.ry },
      });
    } else {
      existing.target = { x: p.x, z: p.z, ry: p.ry };
      if (p.name && existing.label.text !== p.name) existing.label.text = p.name;
    }
  }

  private removePeerAvatar(id: string): void {
    const a = this.peerAvatars.get(id);
    if (!a) return;
    this.peerAvatars.delete(id);
    try {
      this.hud.adt.removeControl(a.label);
      a.label.dispose();
    } catch { /* HUD may already be disposed on exit */ }
    a.root.dispose(false, true);
  }

  /** Smooth interpolation (~120 ms time constant) toward the last received pos. */
  private updatePeerAvatars(dt: number): void {
    if (this.peerAvatars.size === 0) return;
    const k = 1 - Math.exp(-dt / 0.12);
    for (const a of this.peerAvatars.values()) {
      a.cur.x += (a.target.x - a.cur.x) * k;
      a.cur.z += (a.target.z - a.cur.z) * k;
      let dry = a.target.ry - a.cur.ry;
      while (dry > Math.PI) dry -= Math.PI * 2;
      while (dry < -Math.PI) dry += Math.PI * 2;
      a.cur.ry += dry * k;
      a.root.position.x = a.cur.x;
      a.root.position.z = a.cur.z;
      a.root.rotation.y = a.cur.ry;
    }
  }

  /** v9 — a peer advanced the SHARED objective: fold it (dedupe by consumed id). */
  private foldRemoteProgress(iid: string, fromId: string, sceneId: string): void {
    const def = this.def;
    if (!def || this.completed || sceneId !== def.id) return;
    if (iid.startsWith("kit_")) return; // spiritual kit never advances objectives
    if (this.consumed.has(iid)) return; // already counted locally — dedupe
    const idef = this.idefs.get(iid);
    if (!idef || !ADVANCING.has(idef.type)) return;
    this.consumed.add(iid);
    this.progress++;
    // Mirror visually — the item was handled by the peer, so it leaves OUR world
    // too (levers stay: they are re-usable world triggers).
    const m = this.targets.get(iid);
    if (m && idef.type !== "lever_action") m.setEnabled(false);
    const who = this.mp?.peerName(fromId) || t({ fr: "Un pèlerin", en: "A pilgrim" });
    this.updateObjective();
    this.hud.showTip({
      fr: `🤝 ${who} a fait avancer l'objectif !`,
      en: `🤝 ${who} advanced the objective!`,
    });
    if (this.progress >= this.goal) this.complete();
  }

  private handleEvent(e: InteractionEvent): void {
    if (e.kind === "panel") return;
    // Spiritual-kit activities never advance scene objectives (grace, not grind).
    if (e.id.startsWith("kit_")) {
      if (e.kind === "collect" && e.id.startsWith("kit_fruit_")) {
        const name = e.id.substring("kit_fruit_".length).replace(/_/g, " ");
        try {
          const f: string[] = JSON.parse(localStorage.getItem("kq_fruits") || "[]");
          if (!f.includes(name)) f.push(name);
          localStorage.setItem("kq_fruits", JSON.stringify(f));
        } catch { /* ignore */ }
        this.hud.showTip({
          fr: `🍇 Fruit de l'Esprit reçu : ${name} (Galates 5:22-23)`,
          en: `🍇 Fruit of the Spirit received: ${name} (Galatians 5:22-23)`,
        });
      }
      if (e.kind === "lever" && e.id === "kit_victory") {
        this.hud.showTip({
          fr: "🏆 Victoire ! « Grâces soient rendues à Dieu, qui nous donne la victoire par notre Seigneur Jésus-Christ ! » (1 Co 15:57)",
          en: "🏆 Victory! 'Thanks be to God, who gives us the victory through our Lord Jesus Christ!' (1 Cor 15:57)",
        });
        this.worldReact(this.idefs.get(e.id));
      }
      return;
    }
    if (this.completed) return;
    // Lever actions fire a VISIBLE world event on their target (sea parts,
    // gate opens, fire falls…) — the "real elements" bar, not a slideshow.
    if (e.kind === "lever") this.worldReact(this.idefs.get(e.id));
    // Each advancing interactable counts once (levers stay re-clickable).
    if (this.consumed.has(e.id)) return;
    this.consumed.add(e.id);
    this.progress++;
    // v9 multiplayer — share the advance with the group (dedupe by id on
    // every peer via their own consumed set).
    this.mp?.sendProgress(e.id);
    this.updateObjective();
    const def = this.def!;
    if (def.tips[1] && this.progress === 1) this.hud.showTip(def.tips[1]);
    if (this.progress >= this.goal) this.complete();
  }

  /** Animate the world in response to a lever: water parts; anything else rises & glows. */
  private worldReact(lever: InteractableDef | undefined): void {
    const targetId = lever?.targetId;
    if (!targetId) return;
    const target = this.targets.get(targetId);
    if (!target) return;
    const tdef = this.idefs.get(targetId);
    const scene = this.gen.scene;
    const start = performance.now();
    if (tdef?.shape === "water") {
      // The sea PARTS: the water sheet narrows into a corridor and sinks slightly.
      const sx = target.scaling.x;
      const obs = scene.onBeforeRenderObservable.add(() => {
        const k = Math.min(1, (performance.now() - start) / 1800);
        target.scaling.x = sx * (1 - 0.75 * k);
        target.position.y = -0.1 * k;
        if (k >= 1) scene.onBeforeRenderObservable.remove(obs);
      });
    } else {
      // Generic visible reaction: the target rises and pulses golden light.
      const y0 = target.position.y;
      const obs = scene.onBeforeRenderObservable.add(() => {
        const k = Math.min(1, (performance.now() - start) / 1200);
        target.position.y = y0 + Math.sin(k * Math.PI) * 1.4;
        const s = 1 + Math.sin(k * Math.PI) * 0.3;
        target.scaling.setAll(s);
        if (k >= 1) {
          target.scaling.setAll(1);
          scene.onBeforeRenderObservable.remove(obs);
        }
      });
      this.highlight?.addMesh(target, Color3.FromHexString("#ffd76a"));
      setTimeout(() => this.highlight?.removeMesh(target), 1600);
    }
  }

  private updateObjective(): void {
    const def = this.def!;
    const obj = def.objectives[0];
    const label = obj ? t(obj.label) : t({ fr: "Objectif", en: "Objective" });
    this.hud.setObjective(`${label}  (${Math.min(this.progress, this.goal)}/${this.goal})`);
  }

  private complete(): void {
    this.completed = true;
    const def = this.def!;
    const finish = () => {
      try {
        const keys: string[] = JSON.parse(localStorage.getItem("kq_keys") || "[]");
        if (!keys.includes(def.game)) keys.push(def.game);
        localStorage.setItem("kq_keys", JSON.stringify(keys));
      } catch {
        /* ignore */
      }
      this.hud.showKeyReward(t(def.keyName), t(def.lesson), () => {
        this.teardown();
        this.onExit();
      });
    };
    // Quiz gate (grace framing): a RANDOM question from the bank runs before
    // the key; the explanation always shows, then the key is granted either
    // way. Performance is recorded to adapt future sessions.
    const quiz = this.pickQuiz(def);
    if (quiz) {
      openQuiz(this.hud.adt, quiz, (correct) => {
        this.perfrecord(def.game, correct);
        finish();
      });
    } else {
      this.perfrecord(def.game, null);
      finish();
    }
  }

  /** v5 — Spiritual activities kit, added to EVERY scene (ids kit_*). */
  private spiritualKitDefs(def: SceneDef): InteractableDef[] {
    const e = def.env.groundSize / 2 - 5;
    const fruits = ["Amour", "Joie", "Paix", "Patience", "Bonté", "Bénignité", "Fidélité", "Douceur", "Tempérance"];
    const kit: InteractableDef[] = [
      { id: "kit_prayer", type: "panel", label: { fr: "🙏 Lieu de prière", en: "🙏 Place of prayer" }, pos: [-e, 0, e], shape: "stele", color: "#8fb8d8", size: 1.5, panelId: "kit_p_prayer" },
      { id: "kit_verse", type: "panel", label: { fr: "📜 Déclare le verset", en: "📜 Declare the verse" }, pos: [e, 0, e], shape: "stele", color: "#d8c88f", size: 1.5, panelId: "kit_p_verse" },
      { id: "kit_faith", type: "panel", label: { fr: "✨ Pas de foi", en: "✨ Step of faith" }, pos: [-e, 0, -e], shape: "gate", color: "#c8a8e8", size: 1.6, panelId: "kit_p_faith" },
      { id: "kit_victory", type: "lever_action", label: { fr: "🏆 Autel de victoire", en: "🏆 Altar of victory" }, pos: [e, 0, -e], shape: "flame", color: "#f0c850", size: 1.4, holdS: 2, targetId: "kit_victory" },
    ];
    fruits.forEach((f, i) => {
      const ang = (i / fruits.length) * Math.PI * 2;
      kit.push({
        id: `kit_fruit_${f.replace(/ /g, "_")}`,
        type: "pickup",
        label: { fr: `Fruit : ${f}`, en: `Fruit: ${f}` },
        pos: [Math.cos(ang) * (e - 3), 0.4, Math.sin(ang) * (e - 3)],
        shape: "sphere",
        color: ["#e05c5c", "#f0c850", "#8fd88f", "#8fb8d8", "#d88fb8", "#f0a860", "#a8d8d8", "#c8a8e8", "#b8d86a"][i],
        size: 0.5,
      });
    });
    return kit;
  }

  private spiritualKitPanels(def: SceneDef): PanelDef[] {
    return [
      {
        id: "kit_p_prayer",
        title: { fr: "Le lieu secret", en: "The secret place" },
        reference: "Matthieu 6:6",
        passage: {
          fr: "Mais quand tu pries, entre dans ta chambre, ferme ta porte, et prie ton Père qui est là dans le lieu secret; et ton Père, qui voit dans le secret, te le rendra.",
          en: "But you, when you pray, enter into your inner room, and having shut your door, pray to your Father who is in secret, and your Father who sees in secret will reward you openly.",
        },
        explain: {
          fr: "Prends un instant, ici même, pour parler au Père — dans tes mots. La prière n'est pas une mécanique de jeu : c'est une rencontre.",
          en: "Take a moment, right here, to speak to the Father — in your own words. Prayer is not a game mechanic: it is a meeting.",
        },
        application: { fr: "Pour qui veux-tu prier maintenant ?", en: "Who do you want to pray for right now?" },
      },
      {
        id: "kit_p_verse",
        title: { fr: "Déclare la Parole à voix haute", en: "Declare the Word aloud" },
        reference: "Josué 1:8",
        passage: def.lesson,
        explain: {
          fr: "« Que ce livre de la loi ne s'éloigne point de ta bouche » (Jos 1:8). Lis ce verset À VOIX HAUTE — la foi vient de ce qu'on entend (Rm 10:17).",
          en: "'This book of the law shall not depart from your mouth' (Jos 1:8). Read this verse OUT LOUD — faith comes by hearing (Rom 10:17).",
        },
        application: { fr: "Déclare-le trois fois, avec ton cœur.", en: "Declare it three times, from the heart." },
      },
      {
        id: "kit_p_faith",
        title: { fr: "Le pas de foi", en: "The step of faith" },
        reference: "Hébreux 11:1",
        passage: {
          fr: "Or la foi est une ferme assurance des choses qu'on espère, une démonstration de celles qu'on ne voit pas.",
          en: "Now faith is assurance of things hoped for, proof of things not seen.",
        },
        explain: {
          fr: "Chaque scène de ce jeu raconte un pas de foi réel. Quel est le tien aujourd'hui — la chose que tu feras sans tout voir d'avance ?",
          en: "Every scene of this game tells a real step of faith. What is yours today — the thing you will do without seeing everything first?",
        },
        application: { fr: "Nomme UN pas de foi concret pour cette semaine.", en: "Name ONE concrete step of faith for this week." },
      },
    ];
  }

  /** v7 — RANDOM draw from the scene's question bank: prefer the current
   *  difficulty's pool, else the whole bank. A different question (almost)
   *  every session. */
  private pickQuiz(def: SceneDef): QuizDef | undefined {
    if (!def.quizzes.length) return undefined;
    const pool = def.quizzes.filter((q) => q.level === this.difficulty);
    const bank = pool.length ? pool : def.quizzes;
    return bank[Math.floor(this.rand() * bank.length)];
  }

  /* ── v7 adaptive-performance helpers (localStorage kq_perf) ─────────── */
  private perfRead(): { completions: Record<string, number>; lastQuizWrong?: boolean } {
    try {
      return JSON.parse(localStorage.getItem("kq_perf") || "{}");
    } catch {
      return { completions: {} };
    }
  }
  private perfCompletions(game: string): number {
    return this.perfRead().completions?.[game] ?? 0;
  }
  private perfLastQuizWrong(): boolean {
    return this.perfRead().lastQuizWrong === true;
  }
  private perfrecord(game: string, quizCorrect: boolean | null): void {
    try {
      const p = this.perfRead();
      p.completions = p.completions || {};
      p.completions[game] = (p.completions[game] ?? 0) + 1;
      if (quizCorrect !== null) p.lastQuizWrong = !quizCorrect;
      localStorage.setItem("kq_perf", JSON.stringify(p));
    } catch { /* ignore */ }
  }

  private movePlayer(): void {
    const p = this.player;
    if (!p || !this.def) return;
    const dt = this.gen.scene.getEngine().getDeltaTime() / 16;
    const speed = 0.14 * dt;
    let dx = 0;
    let dz = 0;
    if (this.keys.has("w") || this.keys.has("arrowup")) dz -= 1;
    if (this.keys.has("s") || this.keys.has("arrowdown")) dz += 1;
    if (this.keys.has("a") || this.keys.has("arrowleft")) dx -= 1;
    if (this.keys.has("d") || this.keys.has("arrowright")) dx += 1;
    // v9 — GAMEPAD (native Gamepad API, zero deps, fail-open): left stick
    // (deadzone 0.18) + d-pad move the player with the SAME priority as the
    // keyboard — both override the touch tap-to-move destination. buttons[0]
    // (A) is reserved for a future interact binding and ignored for now.
    try {
      const pads = typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : [];
      let pad: Gamepad | null = null;
      for (const c of pads) if (c) { pad = c; break; }
      if (pad) {
        if (!this.gamepadLogged) {
          this.gamepadLogged = true;
          console.log(`[KQ] gamepad detected: ${pad.id} (${pad.axes.length} axes, ${pad.buttons.length} buttons)`);
        }
        const DEAD = 0.18;
        const ax = pad.axes[0] ?? 0;
        const ay = pad.axes[1] ?? 0;
        if (Math.abs(ax) > DEAD) dx += ax;
        if (Math.abs(ay) > DEAD) dz += ay;
        // Standard-mapping d-pad: 12=up 13=down 14=left 15=right.
        if (pad.buttons[12]?.pressed) dz -= 1;
        if (pad.buttons[13]?.pressed) dz += 1;
        if (pad.buttons[14]?.pressed) dx -= 1;
        if (pad.buttons[15]?.pressed) dx += 1;
      }
    } catch { /* fail-open: no Gamepad API → keyboard/touch unchanged */ }
    if (dx === 0 && dz === 0) {
      // touch tap-to-move: walk toward the tapped ground point
      const t = this.moveTarget;
      if (!t) return;
      dx = t.x - p.position.x;
      dz = t.z - p.position.z;
      if (Math.hypot(dx, dz) < 0.4) {
        this.moveTarget = null;
        return;
      }
    } else {
      this.moveTarget = null; // keyboard overrides touch destination
    }
    const len = Math.hypot(dx, dz);
    const half = this.def.env.groundSize / 2 - 1.5;
    const nx = clamp(p.position.x + (dx / len) * speed, -half, half);
    const nz = clamp(p.position.z + (dz / len) * speed, -half, half);
    // v8 obstacles: blocked straight ahead? try sliding along one axis.
    if (!this.hitsSolid(nx, nz)) {
      p.position.x = nx;
      p.position.z = nz;
    } else if (!this.hitsSolid(nx, p.position.z)) {
      p.position.x = nx;
    } else if (!this.hitsSolid(p.position.x, nz)) {
      p.position.z = nz;
    } else if (this.moveTarget) {
      this.moveTarget = null; // destination unreachable — stop cleanly
    }
    p.rotation.y = Math.atan2(dx, dz);
  }

  /** True when (x,z) is inside any solid obstacle's radius (+player 0.45). */
  private hitsSolid(x: number, z: number): boolean {
    for (const s of this.solids) {
      const ddx = x - s.x;
      const ddz = z - s.z;
      if (ddx * ddx + ddz * ddz < (s.r + 0.45) * (s.r + 0.45)) return true;
    }
    return false;
  }

  private onKeyDown = (e: KeyboardEvent) => this.keys.add(e.key.toLowerCase());
  private onKeyUp = (e: KeyboardEvent) => this.keys.delete(e.key.toLowerCase());

  private teardown(): void {
    // v9 multiplayer — drop remote avatars + unhook relay callbacks for this
    // scene (the connection itself lives in main.ts across the parcours).
    for (const id of [...this.peerAvatars.keys()]) this.removePeerAvatar(id);
    if (this.mp) {
      this.mp.onPeerUpsert = null;
      this.mp.onPeerLeft = null;
      this.mp.onRemoteProgress = null;
      this.mp.onStatus = null;
      this.mp.setScene(null);
    }
    if (this.renderObs) {
      this.gen.scene.onBeforeRenderObservable.remove(this.renderObs);
      this.renderObs = null;
    }
    if (this.pointerObs) {
      this.gen.scene.onPointerObservable.remove(this.pointerObs);
      this.pointerObs = null;
    }
    this.moveTarget = null;
    this.groundMesh = null;
    this.built.forEach((n) => n.dispose(false, true));
    this.built = [];
    this.highlight?.dispose();
    this.highlight = null;
    this.player = null;
    this.gen.camera.lockedTarget = null;
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.teardown();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// keep mat imported for tree-shake friendliness / future material tweaks
void mat;
